import type { UserRole } from '../types';
import { OUSTER_ORG_ID, ROLES } from './constants';

export function formatUserRoleLabel(role: string) {
  return role.replace(/_/g, ' ');
}

export function isAdminLevelRole(role?: UserRole | null) {
  return role === 'ADMIN' || role === 'SUPER_ADMIN';
}

export function isSuperAdminRole(role?: UserRole | null) {
  return role === 'SUPER_ADMIN';
}

export function isOusterSuperAdminRole(
  organizationId?: number | null,
  role?: UserRole | null
) {
  return organizationId === OUSTER_ORG_ID && isSuperAdminRole(role);
}

export function canCopyOusterRecording(
  currentOrganizationId?: number | null,
  role?: UserRole | null,
  recordingOrganizationId?: number | null
) {
  return (
    currentOrganizationId === OUSTER_ORG_ID &&
    recordingOrganizationId === OUSTER_ORG_ID &&
    role !== 'BASIC' &&
    Boolean(role)
  );
}

export function getAssignableRoles({
  isOusterUser,
  canAssignSuperAdmin,
  isProvisioned = false,
  currentRole,
}: {
  isOusterUser: boolean;
  canAssignSuperAdmin: boolean;
  isProvisioned?: boolean;
  currentRole?: UserRole;
}): UserRole[] {
  const withCurrentRole = (roles: UserRole[]) => {
    if (!currentRole || roles.includes(currentRole)) {
      return roles;
    }

    return [currentRole, ...roles];
  };

  if (isProvisioned) {
    return withCurrentRole(['ADMIN']);
  }

  if (!isOusterUser) {
    return withCurrentRole(['ADMIN', 'BASIC']);
  }

  if (canAssignSuperAdmin) {
    return withCurrentRole([...ROLES]);
  }

  return withCurrentRole(ROLES.filter((role) => role !== 'SUPER_ADMIN'));
}

/**
 * Default role pre-selected in invite forms.
 * Prefers BASIC when assignable; otherwise the first available role.
 */
export function getDefaultInviteRole(availableRoles: UserRole[]): UserRole {
  if (availableRoles.includes('BASIC')) {
    return 'BASIC';
  }

  return availableRoles[0];
}
